import { supabase } from './supabase'

// ═══════════════════════════════════════════════════════════════════════════
// API base
// ═══════════════════════════════════════════════════════════════════════════
//
// All backend calls go through this prefix. Default `/api`, overridable via the
// `VITE_API_URL` build-time env var.
//
// In dev, vite.config.ts proxies `/api` → http://localhost:8080 and STRIPS the
// `/api` prefix, so `${API_BASE}/health` reaches the FastAPI `/health` route and
// `${API_BASE}/events/<name>/stream` reaches `/events/<name>/stream`. Keep the
// default in sync with the proxy's rewrite rule.

export const API_BASE = import.meta.env.VITE_API_URL || '/api'

// ═══════════════════════════════════════════════════════════════════════════
// Auth helpers
// ═══════════════════════════════════════════════════════════════════════════

/** Current Supabase access token, or null when signed out. */
async function getAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session?.access_token ?? null
}

/**
 * Auth headers for authenticated requests. Returns an empty object when there's
 * no session so callers can spread it unconditionally.
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** Bounce to the login page, preserving where the user was headed. */
function redirectToLogin(): void {
  window.location.href =
    '/login?returnTo=' + encodeURIComponent(window.location.pathname + window.location.search)
}

// ═══════════════════════════════════════════════════════════════════════════
// apiFetch — JSON request helper with auth + 401 handling
// ═══════════════════════════════════════════════════════════════════════════

export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * Authenticated JSON fetch. Attaches the Supabase bearer token, sets the JSON
 * content-type, and parses the response body as `T`. On 401 it redirects to
 * /login and throws. Use for plain request/response endpoints; use
 * `streamWorkflow` for SSE workflow streams.
 *
 * @param endpoint path relative to `API_BASE`, e.g. `/health` or `/foo/bar`
 */
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
  })

  if (!response.ok) {
    if (response.status === 401) {
      redirectToLogin()
    }
    const body = await response.text()
    throw new ApiError(response.status, body || `HTTP ${response.status}`)
  }

  // 204 / empty bodies → undefined; otherwise parse JSON.
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

// ═══════════════════════════════════════════════════════════════════════════
// streamWorkflow — generic SSE client for workflow streams
// ═══════════════════════════════════════════════════════════════════════════
//
// Generalized from the project's per-workflow `streamX` functions. Every
// workflow SSE endpoint follows the same wire shape:
//
//   POST ${API_BASE}<path>            (path e.g. `/events/<name>/stream`)
//   Authorization: Bearer <token>
//   Content-Type: application/json
//   body: <whatever the workflow's input schema expects>
//
//   ← text/event-stream of frames separated by a blank line, each line:
//       data: {"type": "...", ...}\n\n
//     A literal `data: [DONE]` (or a `{"type":"...complete"}` event) ends it.
//
// This helper handles the transport (auth, POST, ReadableStream decode, frame
// splitting, JSON parse, 401 redirect) and dispatches every parsed event to a
// single `onEvent(event)` callback. The caller switches on `event.type` for its
// workflow's specific event names — that keeps the baseline workflow-agnostic.

/** A parsed SSE event. `type` is the discriminator; other fields are payload. */
export interface WorkflowEvent {
  type?: string
  [key: string]: unknown
}

export interface StreamWorkflowCallbacks {
  /** Fired once per parsed `data:` event. Switch on `event.type`. */
  onEvent: (event: WorkflowEvent) => void
  /** Fired on network failure, non-OK HTTP status, or a `session_error`/`error` event. */
  onError: (message: string) => void
  /** Fired exactly once when the stream closes cleanly (reader done / `[DONE]`). */
  onDone: () => void
}

/**
 * Stream a workflow's SSE endpoint.
 *
 * @param path     path relative to `API_BASE`, e.g. `/events/<name>/stream`
 * @param body     request body — serialized to JSON; pass the workflow's input shape
 * @param callbacks { onEvent, onError, onDone }
 *
 * @example
 *   await streamWorkflow('/events/myflow/stream', { content, session_id }, {
 *     onEvent: (ev) => {
 *       if (ev.type === 'content_delta') append(String(ev.delta ?? ''))
 *       else if (ev.type === 'session_complete') finalize(ev)
 *     },
 *     onError: (msg) => setError(msg),
 *     onDone: () => setStreaming(false),
 *   })
 */
export async function streamWorkflow(
  path: string,
  body: unknown,
  callbacks: StreamWorkflowCallbacks
): Promise<void> {
  const authHeaders = await getAuthHeaders()

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(body),
    })
  } catch (e) {
    callbacks.onError(e instanceof Error ? e.message : 'Network error')
    return
  }

  if (!response.ok) {
    if (response.status === 401) {
      redirectToLogin()
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE frames are separated by a blank line. The trailing fragment may be
      // a partial frame; keep it for the next read.
      const frames = buffer.split('\n\n')
      buffer = frames.pop() || ''

      for (const frame of frames) {
        // A frame may span multiple lines; only `data:` lines carry payload.
        for (const line of frame.split('\n')) {
          if (!line.startsWith('data: ')) continue
          const raw = line.slice(6)
          if (raw === '[DONE]') {
            callbacks.onDone()
            return
          }
          let event: WorkflowEvent
          try {
            event = JSON.parse(raw)
          } catch {
            continue
          }
          // Surface error-shaped events through onError; everything else flows
          // to onEvent for the caller to route by `event.type`.
          if (event.type === 'error' || event.type === 'session_error') {
            callbacks.onError(String(event.error ?? 'Unknown error'))
          } else {
            callbacks.onEvent(event)
          }
        }
      }
    }
  } catch (e) {
    callbacks.onError(e instanceof Error ? e.message : 'Stream read error')
    return
  } finally {
    reader.releaseLock()
  }

  callbacks.onDone()
}

// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
// streamCanon — SSE client for the canon workflow (scaffold stub)
// ═══════════════════════════════════════════════════════════════════════════
//
// Typed wrapper over streamWorkflow. The node-builder refines the event mapping
// (one `case '<node>_done'` per node) once the DAG's events firm up.
// See decomposing-workflows/reference/connection.md.

export interface StreamCanonParams {
  session_id?: string | null
  content: string
  run_mode?: 'fast' | 'deep'
}

export interface StreamCanonCallbacks {
  onThinkingDelta?: (delta: string) => void
  onText?: (delta: string) => void
  onDone: (event: WorkflowEvent) => void
  onError: (message: string) => void
}

export interface CanonSessionTurn {
  role: 'user' | 'assistant'
  content: string
  mode: string
  thinking: string | null
}

export async function fetchCanonSession(sessionId: string): Promise<CanonSessionTurn[]> {
  return apiFetch<CanonSessionTurn[]>(`/canon/sessions/${encodeURIComponent(sessionId)}`)
}

export async function streamCanon(
  params: StreamCanonParams,
  callbacks: StreamCanonCallbacks
): Promise<void> {
  await streamWorkflow(
    '/events/canon/stream',
    {
      session_id: params.session_id ?? null,
      content: params.content,
      run_mode: params.run_mode ?? 'fast',
    },
    {
      onEvent: (ev) => {
        switch (ev.type) {
          case 'canon_placeholder':
            callbacks.onText?.(String(ev.message ?? ''))
            break
          case 'thinking':
            callbacks.onThinkingDelta?.(String(ev.thinking ?? ''))
            break
          case 'text':
            callbacks.onText?.(String(ev.text ?? ''))
            break
          case 'session_complete':
            callbacks.onDone(ev)
            break
        }
      },
      onError: callbacks.onError,
      onDone: () => {},
    }
  )
}
// >>> NEW_WORKFLOW_API — commissioning-workflows inserts typed clients here <<<

// ═══════════════════════════════════════════════════════════════════════════
// Health
// ═══════════════════════════════════════════════════════════════════════════

/** Liveness probe — GET ${API_BASE}/health (proxied to the API's /health). */
export async function fetchHealth(): Promise<{ status?: string } & Record<string, unknown>> {
  return apiFetch('/health')
}
